import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, Save, Trash2 } from 'lucide-react'
import {
  Button,
  EmptyState,
  QueryErrorState,
  groupRows,
  PageHeaderBand,
  Popover,
  PopoverMenu,
  PopoverMenuGroup,
  PopoverMenuItem,
  PopoverTrigger,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
  toast,
  type GroupAccent,
  type TableGroup,
} from '@/components/ui'
import {
  addSaved,
  defaultLabelFor,
  loadSaved,
  removeSaved,
  type SavedSearch,
} from '../utils/saved-searches'
import {
  CofaKpiStrip,
  CofaPreviewDrawer,
  DEFAULT_FILTERS,
  DocumentGrid,
  DocumentList,
  DocumentTable,
  groupDocuments,
  useCofaDocuments,
  useCofaStats,
  type CofaClientFilters,
  type CofaKindFilter,
  type DocumentRow,
  type GroupBy,
  type LookupQuery,
  type StatusFilter,
} from '@/shell/services/cofa-registry'

type ViewMode = 'grid' | 'list' | 'table'

// Document-status group labels/accents for the grouped table view.
const STATUS_GROUP_LABEL: Record<string, string> = {
  complete: 'Complete',
  missing_spi: 'Missing SPI',
  missing_vendor: 'Missing vendor',
}
const STATUS_GROUP_ACCENT: Record<string, GroupAccent> = {
  complete: 'success',
  missing_spi: 'warning',
  missing_vendor: 'danger',
}

/**
 * CofA repository — read-only reference library for all users
 * (sales, warehouse, ops, regulatory, admin, viewer). Formerly at
 * `/cofa-docs`; renamed + repurposed at Sprint 04b (2026-04-20) to be
 * the primary sidebar entry labelled "CofA".
 *
 * Persona: sales + warehouse look up what CofAs exist on a given lot
 * and download the file. No mutation surfaces — that lives on the
 * management page (admin + regulatory + operations) at /cofa-docs,
 * rendered by the `cofa-pipeline` module.
 */
export function CofaDocumentsPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filters, setFilters] = useState<CofaClientFilters>(DEFAULT_FILTERS)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [groupBy, setGroupBy] = useState<GroupBy>('vendor')
  const [selected, setSelected] = useState<{
    lotId: number
    tab: 'vendor' | 'spi'
  } | null>(null)
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(() => loadSaved())
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const query = useMemo<LookupQuery>(
    () => ({
      q: debouncedSearch,
      status: filters.status as StatusFilter,
      limit: 200,
      offset: 0,
      scope: filters.includeUsedUp ? 'all' : 'in_stock',
    }),
    [debouncedSearch, filters.status, filters.includeUsedUp],
  )

  const { data, isLoading, isFetching, isError, error, refetch } = useCofaDocuments(query)
  const { data: stats } = useCofaStats()

  const rows = useMemo<DocumentRow[]>(() => data?.lots ?? [], [data])
  const total = data?.total ?? rows.length

  const filtered = useMemo<DocumentRow[]>(() => {
    const vendorNeedle = filters.vendorQuery.trim().toLowerCase()
    const productNeedle = filters.productQuery.trim().toLowerCase()
    return rows.filter((row) => {
      if (filters.spiOnly && !row.is_spi_product) return false
      if (filters.excludeBothMissing && !row.vendor_cofa && !row.spi_cofa) return false
      if (filters.cofaKind === 'vendor' && !row.vendor_cofa) return false
      if (filters.cofaKind === 'spi' && !row.spi_cofa) return false
      if (vendorNeedle && !row.vendor_name.toLowerCase().includes(vendorNeedle)) return false
      if (productNeedle && !row.product_name.toLowerCase().includes(productNeedle)) return false
      return true
    })
  }, [rows, filters])

  const groups = useMemo(() => groupDocuments(filtered, groupBy), [filtered, groupBy])

  // Table-view grouping (mirrors CofA Management). Status renders all three
  // buckets including empty ones; vendor/product/date reuse `groups`.
  const tableGroups = useMemo<TableGroup<DocumentRow>[] | undefined>(() => {
    if (viewMode !== 'table' || groupBy === 'none') return undefined
    if (groupBy === 'status') {
      return groupRows(filtered, {
        keyOf: (r) => r.status,
        groupOrder: ['complete', 'missing_spi', 'missing_vendor'],
        labelOf: (k) => STATUS_GROUP_LABEL[k] ?? k,
        accentOf: (k) => STATUS_GROUP_ACCENT[k],
      })
    }
    return groups as TableGroup<DocumentRow>[]
  }, [viewMode, groupBy, filtered, groups])

  const selectedRow = useMemo(() => {
    if (!selected) return null
    return filtered.find((r) => r.lot_id === selected.lotId) ?? null
  }, [filtered, selected])

  /** When a row is selected without a specific column-click hint, the
   *  preview opens to the kind the page is currently focused on. The
   *  default 'all' falls back to vendor (matches the drawer's prior
   *  default behavior). */
  // Open on the certificate customers receive (SPI CofA for SPI products)
  // unless the user narrowed the page to one source.
  const tabFor = (row: DocumentRow): 'vendor' | 'spi' =>
    filters.cofaKind === 'spi' ? 'spi' : filters.cofaKind === 'vendor' ? 'vendor' : row.is_spi_product ? 'spi' : 'vendor'

  const drawerIndex = selectedRow ? filtered.findIndex((r) => r.lot_id === selectedRow.lot_id) : -1
  const drawerPrev =
    drawerIndex > 0
      ? () => {
          const next = filtered[drawerIndex - 1]!
          setSelected({ lotId: next.lot_id, tab: tabFor(next) })
        }
      : undefined
  const drawerNext =
    drawerIndex >= 0 && drawerIndex < filtered.length - 1
      ? () => {
          const next = filtered[drawerIndex + 1]!
          setSelected({ lotId: next.lot_id, tab: tabFor(next) })
        }
      : undefined

  // `/` focuses search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Library is the REFERENCE surface — no admin affordances. Admins
  // who need to mutate a CofA (rename/replace/remove/upload) jump to
  // the management page at /cofa-docs.
  const canAct = false

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="CofAs"
        description="Vendor and SPI Certificates of Analysis across every tracked lot. Click a document to preview and download."
      >
        <CofaKpiStrip stats={stats} className="ml-auto" />
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search lot, product, vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="View mode"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'table', label: 'Table' },
                { value: 'list', label: 'List' },
                { value: 'grid', label: 'Grid' },
              ]}
            />
            <ToolbarDivider />
            <Select
              aria-label="Source"
              value={filters.cofaKind}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  cofaKind: e.target.value as CofaKindFilter,
                })
              }
            >
              <option value="all">All sources</option>
              <option value="vendor">Vendor CofA</option>
              <option value="spi">SPI CofA</option>
            </Select>
            <Select
              aria-label="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value as StatusFilter,
                })
              }
            >
              <option value="all">All statuses</option>
              <option value="complete">Complete</option>
              <option value="missing_spi">Missing SPI</option>
              <option value="missing_vendor">Missing vendor</option>
            </Select>
            <label className="flex items-center gap-1.5 text-role-section-label">
              Group
              <Select
                aria-label="Group by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              >
                <option value="status">Status</option>
                <option value="vendor">Vendor</option>
                <option value="product">Product</option>
                <option value="product_type">Commercial / Sample</option>
                <option value="date">Expiry month</option>
                <option value="none">None</option>
              </Select>
            </label>
            <ToolbarDivider />
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.spiOnly}
                onChange={(e) => setFilters({ ...filters, spiOnly: e.target.checked })}
              />
              SPI only
            </label>
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.excludeBothMissing}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    excludeBothMissing: e.target.checked,
                  })
                }
              />
              Hide missing both
            </label>
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.includeUsedUp}
                onChange={(e) => setFilters({ ...filters, includeUsedUp: e.target.checked })}
              />
              Include used-up lots
            </label>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {filtered.length.toLocaleString()} of {total.toLocaleString()}
            </ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" iconOnly aria-label="Saved searches">
                  <Save size={14} />
                </Button>
              </PopoverTrigger>
              <PopoverMenu>
                <PopoverMenuGroup label="Saved searches">
                  <PopoverMenuItem
                    icon={Save}
                    onSelect={() => {
                      const label = window.prompt(
                        'Name this search',
                        defaultLabelFor(search, filters),
                      )
                      if (!label) return
                      setSavedSearches(addSaved(label, search, filters))
                      toast.success(`Saved "${label}"`)
                    }}
                  >
                    Save current filters…
                  </PopoverMenuItem>
                  {savedSearches.length > 0 ? (
                    <ul className="mt-1 flex flex-col border-t border-border-subtle pt-1">
                      {savedSearches.map((s) => (
                        <li key={s.id} className="flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              setFilters(s.filters)
                              setSearch(s.search)
                            }}
                            title={s.label}
                            className="min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent px-3 py-1.5 text-left text-sm text-primary hover:bg-surface-hover"
                          >
                            {s.label}
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${s.label}`}
                            onClick={() => setSavedSearches(removeSaved(s.id))}
                            className="mr-2 inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
                          >
                            <Trash2 size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </PopoverMenuGroup>
              </PopoverMenu>
            </Popover>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="documents"
            />
          ) : filtered.length === 0 && !isLoading ? (
            <EmptyState
              variant="no-match"
              title="No documents match"
              description="Try clearing a filter or broadening the search."
              action={
                <Button variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)}>
                  Reset filters
                </Button>
              }
            />
          ) : viewMode === 'grid' ? (
            <DocumentGrid
              groups={groups}
              selectedId={selected?.lotId ?? null}
              onSelect={(row) => setSelected({ lotId: row.lot_id, tab: tabFor(row) })}
            />
          ) : viewMode === 'list' ? (
            <DocumentList
              groups={groups}
              selectedId={selected?.lotId ?? null}
              onSelect={(row) => setSelected({ lotId: row.lot_id, tab: tabFor(row) })}
              canAct={canAct}
              onRequestBatchUpload={() => {}}
            />
          ) : (
            <DocumentTable
              loading={isLoading}
              rows={filtered}
              groups={tableGroups}
              onSelect={(row) => setSelected({ lotId: row.lot_id, tab: tabFor(row) })}
              onSelectKind={(row, tab) => setSelected({ lotId: row.lot_id, tab })}
              canAct={canAct}
              onRequestBatchUpload={() => {}}
              moduleId="cofa-documents"
            />
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-2xs text-secondary">
          <span>
            {(stats?.with_vendor_cofa ?? 0).toLocaleString()} vendor ·{' '}
            {(stats?.with_spi_cofa ?? 0).toLocaleString()} SPI ·{' '}
            {(stats?.pending_approval ?? 0).toLocaleString()} pending
          </span>
          <span>/ focus search · click card to preview · Esc close</span>
        </footer>
      </div>

      <CofaPreviewDrawer
        row={selectedRow}
        initialTab={selected?.tab ?? 'vendor'}
        canAct={canAct}
        onClose={() => setSelected(null)}
        onPrev={drawerPrev}
        onNext={drawerNext}
      />
    </div>
  )
}
